import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ar } from './locales/ar';
import { de } from './locales/de';
import { en } from './locales/en';
import { fr } from './locales/fr';
import type { Direction, LocaleCode, LocaleDescriptor, TranslationKey, Translations } from './types';

export type { Direction, LocaleCode, LocaleDescriptor, TranslationKey } from './types';


export const LOCALES: readonly LocaleDescriptor[] = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'fr', label: 'Français', dir: 'ltr' },
  { code: 'de', label: 'Deutsch', dir: 'ltr' },
];

const MESSAGES: Record<LocaleCode, Translations> = { en, ar, fr, de };
const DEFAULT_LOCALE: LocaleCode = 'en';
const STORAGE_KEY = 'ita6.locale';

function isLocaleCode(value: string): value is LocaleCode {
  return LOCALES.some((locale) => locale.code === value);
}

function directionOf(locale: LocaleCode): Direction {
  return LOCALES.find((entry) => entry.code === locale)?.dir ?? 'ltr';
}

/**
 * Stored preference first, then the browser's preferred languages, then English.
 * Matching is done on the primary subtag so `fr-CA` resolves to `fr`.
 */
function detectLocale(): LocaleCode {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && isLocaleCode(stored)) return stored;
  } catch {
    // localStorage can be unavailable; fall through to browser detection.
  }

  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const candidate of candidates) {
    const base = candidate.toLowerCase().split('-')[0];
    if (isLocaleCode(base)) return base;
  }
  return DEFAULT_LOCALE;
}

export interface TranslateParams {
  [name: string]: string | number;
}

/** Signature of the translation function (also used by non-React helpers). */
export type TranslateFn = (key: TranslationKey, params?: TranslateParams) => string;

function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : match,
  );
}

export interface I18nContextValue {
  locale: LocaleCode;
  dir: Direction;
  setLocale: (locale: LocaleCode) => void;
  /** Translate a key, substituting `{placeholder}` values when supplied. */
  t: TranslateFn;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * Localisation provider.
 *
 * The active locale drives `<html lang>` and `<html dir>`, so RTL is handled
 * structurally (logical CSS properties) rather than by mirroring individual
 * components.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(() => detectLocale());
  const dir = directionOf(locale);

  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = dir;
  }, [locale, dir]);

  const setLocale = useCallback((next: LocaleCode): void => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Preference won't persist; the UI still switches for this session.
    }
  }, []);

  const t = useCallback(
    (key: TranslationKey, params?: TranslateParams): string =>
      interpolate(MESSAGES[locale][key] ?? en[key] ?? key, params),
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, dir, setLocale, t }),
    [locale, dir, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider.');
  }
  return context;
}
